<script setup lang="ts">
import { computed } from 'vue';
import { trackingVisasqNowEvent } from '@/api/tracking';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import Divider from '@/components/common/divider.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { VisasqNowSectionName } from '@/types';
import {
  SURVEY_ERROR_MESSAGES,
  SURVEY_HISTORIES_PAGE_NAME,
} from '@/utils/survey/constants';
import { VISASQ_NOW_FEATURE_NAME } from '@/utils/visasq/constants';

interface Props {
  enableAnimation: boolean;
  isGenerating: boolean;
  isError: boolean;
  surveyHistoryQuestion?: string;
  title?: string;
  background?: string;
  question?: string;
  sampleAnswer?: string;
  // 以降は計測のみで使用
  id: number; // visasq_now_question_draftsテーブルのid
  sessionId: string; // surveysテーブルのuuid
}

/**
 * Props
 */

const props = withDefaults(defineProps<Props>(), {
  surveyHistoryQuestion: '',
  title: '',
  background: '',
  question: '',
  sampleAnswer: '',
});

/**
 * Constants
 */

const VISASQ_NOW_URL =
  'https://client-portal.visasq.com/?utm_source=stockmark&utm_medium=referral&utm_campaign=clientportal';

/**
 * Local State
 */

const { createSnackbar } = useSnackbar();

const visasqNowQuestionDraftItem = computed<
  Array<{
    title: VisasqNowSectionName;
    content: string;
  }>
>(() => {
  return [
    {
      title: '案件タイトル',
      content: props.title,
    },
    {
      title: '調査背景と目的',
      content: props.background,
    },
    {
      title: '質問文',
      content: props.question,
    },
    {
      title: '回答例',
      content: props.sampleAnswer,
    },
  ];
});

// アニメーション名を動的に計算
const transitionName = computed(() => {
  return props.enableAnimation ? 'fade' : '';
});

/**
 * Handlers
 */

const copyField = async (
  content: string,
  sectionName: VisasqNowSectionName,
) => {
  try {
    await navigator.clipboard.writeText(content);

    createSnackbar({
      message: `コピーしました`,
      type: 'success',
    });
  } catch (_e) {
    createSnackbar({
      message: `コピーできませんでした`,
      type: 'error',
    });
  } finally {
    // NOTE: 要約サンプルで初回の深掘りをしたタイミングで、history APIを使用してブラウザの検索バーURLを無理やりパス変更しており、
    // vue-routerで値を取得できないので、window.location.hashを使用している
    const currentPath = window.location.hash;
    const pageName = SURVEY_HISTORIES_PAGE_NAME;

    // 計測
    await trackingVisasqNowEvent('visasq_request_copy', {
      page: {
        name: pageName,
        url: currentPath.replace('#', '').split('?')[0], // WARN: クエリパラメータがこの時点であることはないが、念の為機微情報であるため除外しておく
      },
      feature: VISASQ_NOW_FEATURE_NAME,
      visasq_now_question_draft_id: props.id,
      session_id: props.sessionId,
      section_name: sectionName,
    });
  }
};
</script>

<template>
  <template v-if="props.isError">
    <!-- エラー -->
    <p class="error-message">
      {{ SURVEY_ERROR_MESSAGES.default }}
    </p>
  </template>
  <div v-else class="visasq-now-question-draft">
    <div class="content">
      <a :href="VISASQ_NOW_URL" target="_blank" rel="noopener noreferrer">
        <img
          class="visasq-now-logo"
          src="@/assets/logo_visasq_now.png"
          alt="ビザスクnowのロゴ"
        />
      </a>
      <h2 class="c-title-bold--xl section-title">
        業界のエキスパートに質問するための依頼文を生成
      </h2>
    </div>
    <Divider />
    <div class="content">
      <Transition :name="transitionName" mode="out-in">
        <!-- ローディング -->
        <div v-if="props.isGenerating" key="loading" class="loading-content">
          <div class="loading-spinner">
            <DgrLoading size="full" />
          </div>
          <span> 依頼文を生成しています </span>
        </div>
        <!-- 生成完了 -->
        <div v-else key="completed" class="completed-content">
          <div class="description">
            以下は、<a
              class="visasq-now-form-link"
              :href="VISASQ_NOW_URL"
              target="_blank"
              rel="noopener noreferrer"
              >ビザスクnow</a
            >で業界のエキスパートに「{{
              props.surveyHistoryQuestion
            }}」に関する質問をするための依頼文の例です。
          </div>
          <div v-for="(item, index) in visasqNowQuestionDraftItem" :key="index">
            <div :class="{ 'margin-top-24': index !== 0 }">
              <div class="title-with-copy-icon">
                <h3 class="c-title-bold--xm">{{ item.title }}</h3>
                <VTooltip placement="top">
                  <div
                    class="copy-icon"
                    @click="copyField(item.content, item.title)"
                  >
                    <DgrIcon name="copy" size="small" class="c-icon" />
                  </div>
                  <template #popper>
                    <div class="popper-container c-text-regular--s">
                      <span> コピーする </span>
                    </div>
                  </template>
                </VTooltip>
              </div>
              <p class="text-with-newlines c-text-regular--xm">
                {{ item.content }}
              </p>
            </div>
          </div>
          <p class="disclaimer c-text-regular--m">
            依頼文はビザスクの要件を考慮した上で生成されていますが、正確性を保証するものではありません。必要に応じて修正を依頼させていただくことがありますので、ご了承ください。
          </p>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.disclaimer {
  margin: 16px 0px 8px;
  color: $color-gray800;
}

.visasq-now-question-draft {
  margin-top: 16px;
  background-color: $color-white;
  border-radius: 4px;
  border: solid 1px $color-gray400;
}

.content {
  padding: 16px;
}

.visasq-now-logo {
  max-width: 120px;
  height: auto;
}

.section-title {
  margin-top: 10px;
}

.description {
  margin-bottom: 16px;
}

.visasq-now-form-link {
  text-decoration: underline;
}

.title-with-copy-icon {
  display: flex;
  gap: 4px;
  justify-content: space-between;
  align-items: center;
}

.text-with-newlines {
  white-space: pre-wrap;
  line-height: 1.7;
}

.copy-icon {
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;

  // paddingの範囲も含めてホバーの色を変える
  &:hover {
    background-color: $color-gray400;
  }
}

.margin-top-24 {
  margin-top: 24px;
}

/* フェードインアウトアニメーション */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* ローディングのスタイル */
.loading-spinner {
  height: 24px;
  width: 24px;
}

.loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  font-size: 14px;
  padding: 16px;
  height: 24px;
}

/* エラーメッセージのスタイル */
.error-message {
  margin: 16px 0;
  padding: 16px 0;
  border-bottom: 1px solid $color-gray400;
}
</style>
